import { PersonalityFormState, OfficialPersonalitySchema, Archetype, Temperament, CommunicationStyle, ToneOption, SyntaxStyle, LLMProvider } from '../types';
import { validatePersonality, compilePersonality } from '../services/api';

const SUPPORTED_ARCHETYPES: Archetype[] = ["sage", "explorer", "rebel", "caregiver", "everyman"];

const LEGACY_ARCHETYPE_MAP: Record<string, Archetype> = {
  scientist: "sage",
  adventurer: "explorer",
  motivator: "sage",
  academic: "sage",
  charming: "explorer",
  trendy: "rebel",
  leader: "sage",
  caregiver: "caregiver",
  rebel: "rebel",
  explorer: "explorer",
  sage: "sage",
  everyman: "everyman",
  visionary: "explorer"
};

const DEFAULT_FILLERS_BY_TONE: Record<ToneOption, string[]> = {
  friendly: ["hey", "you know"],
  professional: ["to clarify", "in summary"],
  casual: ["so", "like"],
  formal: ["allow me to clarify", "moreover"],
  warm: ["let's take a moment", "I appreciate that"],
  helpful: ["let's walk through this", "here's the idea"],
  patient: ["take your time", "whenever you're ready"],
  confident: ["trust me", "believe me"],
  humorous: ["just kidding", "fun fact"],
  serious: ["to be precise", "strictly speaking"],
  empathetic: ["I understand", "I hear you"],
  supportive: ["I'm here for you", "we can handle this"],
  inspiring: ["imagine this", "picture that"],
  curious: ["let's explore", "I'm wondering"],
  thoughtful: ["let's reflect", "consider this"]
};

/**
 * Transforms internal form state to official API schema
 * Matches the official LuminoraCore personality JSON structure
 */
export function transformToOfficialSchema(formState: PersonalityFormState): OfficialPersonalitySchema {
  // Determine archetype based on traits (using official enum values)
  const getArchetype = (): Archetype => {
    const userValue = formState.core_traits?.archetype;
    if (userValue && SUPPORTED_ARCHETYPES.includes(userValue)) {
      return userValue;
    }

    if (userValue && LEGACY_ARCHETYPE_MAP[userValue]) {
      return LEGACY_ARCHETYPE_MAP[userValue];
    }

    // Auto-detect based on parameters (restricted to supported archetypes)
    if (formState.advanced_parameters.empathy > 0.7) return "caregiver";
    if (formState.advanced_parameters.formality > 0.7) return "sage";
    if (formState.advanced_parameters.humor > 0.6) return "explorer";
    if (formState.advanced_parameters.creativity > 0.6) return "rebel";
    return "everyman"; // Default balanced archetype
  };

  // Determine temperament (using official enum values)
  const getTemperament = (): Temperament => {
    if (formState.core_traits?.temperament) {
      return formState.core_traits.temperament;
    }
    if (formState.advanced_parameters.humor > 0.6) return "playful";
    if (formState.advanced_parameters.formality > 0.7) return "serious";
    if (formState.advanced_parameters.directness > 0.7) return "energetic";
    if (formState.advanced_parameters.empathy < 0.3) return "thoughtful";
    return "calm";
  };

  // Determine communication style (using official enum values)
  const getCommunicationStyle = (): CommunicationStyle => {
    if (formState.core_traits?.communication_style) {
      return formState.core_traits.communication_style;
    }
    if (formState.advanced_parameters.formality > 0.7) return "formal";
    if (formState.advanced_parameters.directness > 0.7) return "direct";
    if (formState.advanced_parameters.humor > 0.6) return "casual";
    if (formState.advanced_parameters.creativity > 0.6) return "poetic";
    return "conversational";
  };

  // Generate tone array based on parameters (using official enum values)
  const getTone = (): ToneOption[] => {
    if (formState.linguistic_profile?.tone && formState.linguistic_profile.tone.length > 0) {
      return formState.linguistic_profile.tone;
    }
    const tones: ToneOption[] = [];
    if (formState.advanced_parameters.empathy > 0.6) tones.push("warm");
    if (formState.advanced_parameters.formality > 0.6) tones.push("professional");
    if (formState.advanced_parameters.humor > 0.5) tones.push("humorous");
    if (formState.advanced_parameters.formality < 0.4) tones.push("casual");
    if (formState.advanced_parameters.directness > 0.7) tones.push("confident");
    if (formState.advanced_parameters.empathy > 0.7) tones.push("supportive");
    return tones.length > 0 ? tones : ["friendly"];
  };

  const buildLinguisticProfile = () => {
    const tone = getTone();
    const syntax = (formState.linguistic_profile?.syntax || "varied") as SyntaxStyle;
    const vocabulary = formState.linguistic_profile?.vocabulary || [];
    const fillersFromState = formState.linguistic_profile?.fillers || [];
    const punctuation = formState.linguistic_profile?.punctuation_style || "moderate";

    const fallbackFillers =
      tone
        .flatMap(t => DEFAULT_FILLERS_BY_TONE[t] || [])
        .filter(Boolean);

    const fillers = fillersFromState.length > 0
      ? fillersFromState
      : (fallbackFillers.length > 0 ? Array.from(new Set(fallbackFillers)) : ["well", "let's think"]);

    return {
      tone,
      syntax,
      vocabulary,
      fillers,
      punctuation_style: punctuation
    };
  };

  // Ensure behavioral_rules has at least one rule (REQUIRED field)
  const getBehavioralRules = (): string[] => {
    if (formState.behavioral_rules && formState.behavioral_rules.length > 0) {
      return formState.behavioral_rules;
    }
    // Generate default rule based on personality traits
    const rules: string[] = [];
    if (formState.advanced_parameters.empathy > 0.6) {
      rules.push("Always respond with empathy and understanding");
    }
    if (formState.advanced_parameters.formality > 0.6) {
      rules.push("Maintain a professional and respectful tone");
    }
    if (formState.advanced_parameters.humor > 0.5) {
      rules.push("Use appropriate humor to engage the user");
    }
    // If no rules generated, use a default
    return rules.length > 0 ? rules : ["Always be helpful, respectful, and clear in communication"];
  };

  // Build compatibility array (ensure at least one provider - REQUIRED)
  const getCompatibility = (): LLMProvider[] => {
    // Default to all major providers
    return ["openai", "anthropic", "llama", "mistral", "cohere", "google"];
  };

  // Build result with all REQUIRED fields
  const result: OfficialPersonalitySchema = {
    // 1. REQUIRED: Persona
    persona: {
      name: formState.name || "Unnamed Personality",
      version: formState.version || "1.0.0",
      description: formState.description || `AI personality: ${formState.name || "Custom Personality"}`,
      author: formState.author || "LuminoraCore Canvas",
      language: formState.language || "en",
      compatibility: getCompatibility(),
      tags: formState.tags || []
    },
    // 2. REQUIRED: Core traits
    core_traits: {
      archetype: getArchetype(),
      temperament: getTemperament(),
      communication_style: getCommunicationStyle()
    },
    // 3. REQUIRED: Linguistic profile
    linguistic_profile: buildLinguisticProfile(),
    // 4. REQUIRED: Behavioral rules (must have at least one)
    behavioral_rules: getBehavioralRules()
  };

  // 5. OPTIONAL: Advanced parameters (include if present)
  if (formState.advanced_parameters) {
    result.advanced_parameters = {
      verbosity: formState.advanced_parameters.verbosity,
      formality: formState.advanced_parameters.formality,
      humor: formState.advanced_parameters.humor,
      empathy: formState.advanced_parameters.empathy,
      creativity: formState.advanced_parameters.creativity,
      directness: formState.advanced_parameters.directness
    };
  }

  // 6. OPTIONAL: Trigger responses
  if (formState.trigger_responses) {
    result.trigger_responses = formState.trigger_responses;
  }

  // 7. OPTIONAL: Safety guards
  if (formState.safety_guards) {
    result.safety_guards = formState.safety_guards;
  }

  // 8. OPTIONAL: Examples
  if (formState.examples) {
    result.examples = formState.examples;
  }

  // 9. OPTIONAL: Metadata
  if (formState.metadata) {
    result.metadata = formState.metadata;
  } else {
    // Auto-generate metadata if not provided
    result.metadata = {
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }

  return result;
}

/**
 * Generates personality JSON using the official API schema
 * Note: storage_config is NOT included as it's backend configuration, not personality data
 */
export function generatePersonalityJSON(formState: PersonalityFormState): string {
  const officialSchema = transformToOfficialSchema(formState);
  return JSON.stringify(officialSchema, null, 2);
}

/**
 * Compiles personality using the API
 * Falls back to local generation if API fails
 */
export async function generatePersonalityPrompt(
  formState: PersonalityFormState,
  provider: string = 'deepseek'
): Promise<string> {
  try {
    const officialSchema = transformToOfficialSchema(formState);
    const response = await compilePersonality(officialSchema, provider);
    return response.compiled_prompt;
  } catch (error) {
    console.warn('API compilation failed, using local fallback:', error);
    // Fallback to local generation
    return generatePersonalityPromptLocal(formState);
  }
}

/**
 * Local prompt generation (fallback)
 */
export function generatePersonalityPromptLocal(formState: PersonalityFormState): string {
  const traits = formState.advanced_parameters;
  let prompt = `You are ${formState.name}, an AI assistant with the following characteristics:\n\n`;
  
  prompt += `- Empathy level: ${(traits.empathy * 10).toFixed(1)}/10\n`;
  prompt += `- Formality: ${(traits.formality * 10).toFixed(1)}/10\n`;
  prompt += `- Humor: ${(traits.humor * 10).toFixed(1)}/10\n`;
  prompt += `- Verbosity: ${(traits.verbosity * 10).toFixed(1)}/10\n`;
  prompt += `- Directness: ${(traits.directness * 10).toFixed(1)}/10\n`;
  prompt += `- Creativity: ${(traits.creativity * 10).toFixed(1)}/10\n`;
  prompt += `\n`;
  
  if (formState.hierarchical_config?.enabled) {
    prompt += `You have an affinity system that modifies your behavior based on the relationship:\n`;
    prompt += `- Current level: ${formState.hierarchical_config.relationship_levels[0]?.name || 'Unknown'}\n\n`;
  }
  
  if (formState.memory_preferences) {
    prompt += `You have access to conversation memory and can remember:\n`;
    prompt += `- User personal information\n`;
    prompt += `- User preferences and interests\n`;
    prompt += `- Past conversation topics\n\n`;
  }
  
  prompt += `Respond in a way that matches these characteristics.`;
  
  return prompt;
}

/**
 * Generates Python SDK integration code
 */
export function generatePythonCode(formState: PersonalityFormState): string {
  const officialSchema = transformToOfficialSchema(formState);
  return `from luminoracore_sdk import LuminoraCoreClient

# Load personality
client = LuminoraCoreClient()
await client.load_personality("${formState.name}", ${JSON.stringify(officialSchema, null, 2)})

# Use the personality
response = await client.send_message(session_id, "Hello!")`;
}

/**
 * Generates N8N/Make.com integration code
 */
export async function generateN8NCode(
  formState: PersonalityFormState,
  compiledPrompt?: string
): Promise<string> {
  const officialSchema = transformToOfficialSchema(formState);
  
  // Use API compiled prompt if available, otherwise generate locally
  const prompt = compiledPrompt || await generatePersonalityPrompt(formState);
  
  return `// N8N / Make.com Integration Code
const personality = ${JSON.stringify(officialSchema, null, 2)};

// Use in HTTP Request node
const prompt = \`${prompt}\`;

// Call your LLM
const response = await $http.request({
  method: 'POST',
  url: 'https://api.openai.com/v1/chat/completions',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: {
    model: 'gpt-4',
    messages: [
      { role: 'system', content: prompt },
      { role: 'user', content: $input.item.json.message }
    ]
  }
});

return response.data;`;
}

/**
 * Generates preview response based on personality parameters AND message content
 * Now generates contextual responses that actually address the user's message
 */
export function generatePreviewResponse(formState: PersonalityFormState, message: string): string {
  const { empathy, formality, humor, verbosity, directness, creativity } = formState.advanced_parameters;
  const msg = message.toLowerCase().trim();
  
  let response = "";
  
  // Detect message type and generate contextual response
  const isGreetingQuestion = /^(hi|hello|hey|how are you|how's it going|how do you feel|what's up)/i.test(msg);
  const isPersonalInfo = /(visit|going to|will go|travel|family|parents|friends|work|job|study|school)/i.test(msg);
  const isQuestion = /\?/.test(msg) || /^(what|where|when|why|how|who|can you|will you|do you)/i.test(msg);
  const isStatement = !isQuestion && msg.length > 5;
  
  // Greeting based on formality (only if greeting or first interaction)
  if (isGreetingQuestion || msg.length < 10) {
    if (formality > 0.7) {
      response = "Good morning. ";
    } else if (formality > 0.4) {
      response = "Hello! ";
    } else if (formality > 0.2) {
      response = "Hi! ";
    } else {
      response = "Hey! ";
    }
  }
  
  // Memory reference
  if (formState.memory_preferences && !isGreetingQuestion) {
    response += "I remember our last chat! ";
  }
  
  // Generate contextual response based on message content
  if (isGreetingQuestion) {
    // Response to greetings based on empathy and openness
    if (empathy > 0.7 && directness > 0.6) {
      response += "I'm doing wonderfully, thank you so much for asking! ";
    } else if (empathy > 0.7 && directness < 0.3) {
      response += "I'm well, thank you. ";
    } else if (empathy > 0.4) {
      response += "I'm doing well, thanks. ";
    } else {
      response += "Fine. ";
    }
  } else if (isPersonalInfo) {
    // Respond to personal information/travel/family statements
    let contextualResponse = "";
    
    if (/(visit|going to|will visit)/i.test(msg)) {
      if (empathy > 0.6) {
        contextualResponse = verbosity > 0.6 
          ? "That sounds wonderful! I hope you have a great time. " 
          : "That sounds great! ";
      } else {
        contextualResponse = verbosity > 0.6 
          ? "I hope it goes well for you. " 
          : "Good luck. ";
      }
    } else if (/(travel|trip)/i.test(msg)) {
      contextualResponse = empathy > 0.6
        ? "How exciting! Where are you planning to go? "
        : "Where are you going? ";
    } else if (/(parents|family)/i.test(msg)) {
      contextualResponse = empathy > 0.7
        ? "That's lovely that you're spending time with family. "
        : "I hope you enjoy your time together. ";
    } else {
      // Generic acknowledgment
      contextualResponse = empathy > 0.5
        ? "Thanks for sharing that with me. "
        : "I understand. ";
    }
    
    // Adjust tone based on formality
    if (formality > 0.6) {
      contextualResponse = contextualResponse.replace(/That sounds|How exciting|That's lovely/gi, 
        m => m === "That sounds" ? "That certainly sounds" : m);
    }
    
    response += contextualResponse;
  } else if (isQuestion) {
    // Respond to questions
    if (empathy > 0.6) {
      response += verbosity > 0.6
        ? "That's a great question. Let me help you with that. "
        : "Let me help with that. ";
    } else {
      response += verbosity > 0.6
        ? "I can help you with that. "
        : "Sure. ";
    }
  } else if (isStatement) {
    // Respond to statements
    if (empathy > 0.6) {
      response += verbosity > 0.6
        ? "I understand. Thank you for sharing that. "
        : "I understand. ";
    } else {
      response += verbosity > 0.6
        ? "Noted. "
        : "Okay. ";
    }
  }
  
  // Humor level affects the tone - only add if humor is significant AND appropriate
  if (!isPersonalInfo && !isStatement) {
    if (humor > 0.7) {
      response += creativity > 0.6 ? "Ready to turn ideas into magic! 😊 " : "Ready to face the day with a smile! 😊 ";
    } else if (humor > 0.5 && !isQuestion) {
      response += "Feeling good! ";
    }
    // Low humor = serious tone, no emojis, no cheerful phrases
  }
  
  // Closing based on openness and verbosity - only if no specific response was given
  if (!isPersonalInfo && !isQuestion && !isStatement) {
    if (directness < 0.3) {
      if (verbosity > 0.7) {
        response += "How can I assist you?";
      } else if (verbosity < 0.3) {
        response += "What do you need?";
      } else {
        response += "How can I help?";
      }
    } else if (directness > 0.6) {
      if (verbosity > 0.7) {
        response += "I'm really excited to help you with whatever you need! ";
      } else if (verbosity < 0.3) {
        response += "What can I do for you?";
      } else {
        response += "How can I help you today?";
      }
    } else {
      if (verbosity > 0.7) {
        response += "How can I help you today?";
      } else if (verbosity < 0.3) {
        response += "What do you need?";
      } else {
        response += "How can I help you today?";
      }
    }
  } else if (isQuestion && verbosity > 0.5) {
    // Add helpful closing for questions if verbose
    response += "What would you like to know?";
  }
  
  // Add affinity mode if enabled
  if (formState.hierarchical_config?.enabled) {
    response += ` (${formState.hierarchical_config.relationship_levels[0]?.name || 'Unknown'} mode)`;
  }
  
  // Very low verbosity = truncate to first sentence
  if (verbosity < 0.3) {
    const sentences = response.split(/[.!?]/).filter(s => s.trim().length > 0);
    if (sentences.length > 0) {
      response = sentences[0].trim() + (response.match(/[.!?]/) ? response.match(/[.!?]/)![0] : '.');
    }
  }
  
  return response.trim();
}

/**
 * Downloads a file with the given content
 */
export function downloadFile(content: string, filename: string, type = 'application/json'): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
